/**
 * @file ticket-booking-api.service.ts
 * @description This service provides API methods for ticket booking functionality in the application.
 * It extends the `BaseHttpService` to leverage common HTTP methods and handles API interactions
 * related to event registration, registration types, payment initiation, and registration details.
 *
 * @author Jyoti
 * @date 19 August 2025
 */

import { Injectable } from "@angular/core";
import {
  BaseHttpService,
  HttpResult,
} from "../../core/services/base-http.service";
import { BaseResponse } from "../../shared/models/base-response.model";
import { Observable } from "rxjs/internal/Observable";
import {
  initiatePaymentResModel,
  registerTypeReqBodyModel,
  registerTypeResModel,
  registrationDetailsResModel,
  TicketBookingModel,
  VerifyRazorpayPaymentRequest,
  VerifyRazorpayPaymentResponse,
} from "./ticket-booking.model";
import { environment } from "../../../environments/environment";
import { apiEndpoints } from "../../core/constants/api-strings";

@Injectable({
  providedIn: "root",
})
export class TicketBookingApiService extends BaseHttpService {
  /**
   * @constructor
   * Initializes the service by calling the parent `BaseHttpService` constructor.
   */
  constructor() {
    super();
  }

  // Base URL for farmer-related APIs
  private readonly farmerBaseUrl = environment.farmer_base_url;

  // Logged-in farmer's ID
  private farmerId = this.storage.getLoggedUserId();

  // Event code, defaults to 'KISAN25' if not set
  private eventcode = this.storage.getEventCode() || "KISAN25";

  /**
   * @method getEventRegisterType
   * @description Fetches the registration types for the current event.
   * @returns {Observable<HttpResult<BaseResponse<TicketBookingModel>>>} Observable containing the registration types.
   */
  getEventRegisterType(): Observable<
    HttpResult<BaseResponse<TicketBookingModel>>
  > {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.registerTypes
      }`;
    return this.get<BaseResponse<TicketBookingModel>>(serviceName, {}, true);
  }

  /**
   * @method addRegisterType
   * @description Adds a new registration type for the current event.
   * @param {registerTypeReqBodyModel} data - The request body containing registration type details.
   * @returns {Observable<HttpResult<BaseResponse<registerTypeResModel>>>} Observable containing the response.
   */
  addRegisterType(
    data: registerTypeReqBodyModel
  ): Observable<HttpResult<BaseResponse<registerTypeResModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.getAddRegistrationDetails
      }`;
    return this.post<BaseResponse<registerTypeResModel>>(
      serviceName,
      data,
      {},
      true
    );
  }

  /**
   * @method initiatePayment
   * @description Initiates a payment for a specific registration summary.
   * @param {string} summaryId - The ID of the registration summary.
   * @returns {Observable<HttpResult<BaseResponse<initiatePaymentResModel>>>} Observable containing the payment initiation response.
   */
  initiatePayment(
    summaryId: string
  ): Observable<HttpResult<BaseResponse<initiatePaymentResModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.getAddRegistrationDetails +
      summaryId +
      apiEndpoints.farmerRegister.initiateRazorpayPayment
      }`;
    return this.post<BaseResponse<initiatePaymentResModel>>(
      serviceName,
      "",
      {},
      true
    );
  }

  /**
   * @method getRegistrationDetails
   * @description Fetches the registration details for a specific summary ID.
   * @param {string | null} summaryId - The ID of the registration summary.
   * @returns {Observable<HttpResult<BaseResponse<registrationDetailsResModel>>>} Observable containing the registration details.
   */
  getRegistrationDetails(
    summaryId: string | null
  ): Observable<HttpResult<BaseResponse<registrationDetailsResModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.getAddRegistrationDetails +
      summaryId
      }`;
    return this.get<BaseResponse<registrationDetailsResModel>>(
      serviceName,
      {},
      true
    );
  }

  /**
   * Verifies Razorpay payment for ticket booking.
   * @param id - The ID of the farmer.
   * @param summaryId - The summary ID for the booking.
   * @param payload - The payload containing payment details.
   * @returns Observable with verification response.
   */
  verifyRazorpayPayment(
    summaryId: string,
    payload: VerifyRazorpayPaymentRequest
  ): Observable<HttpResult<BaseResponse<VerifyRazorpayPaymentResponse>>> {
    const id = this.farmerId;
    const url = `${environment.farmer_base_url +
      id +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.getAddRegistrationDetails +
      summaryId +
      apiEndpoints.farmerRegister.verifyRazorpayPayment
      }`;

    // Use the inherited 'post' method to ensure the result is wrapped as HttpResult
    return this.post<BaseResponse<VerifyRazorpayPaymentResponse>>(
      url,
      payload,
      {},
      true
    );
  }

  // Records payment failure for ticket booking.
  paymentFailure(
    summaryId: string,
    payload: any
  ): Observable<HttpResult<BaseResponse<any>>> {
    const id = this.farmerId;
    const url = `${environment.farmer_base_url +
      id +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.getAddRegistrationDetails +
      summaryId +
      apiEndpoints.farmerRegister.failurePayment
      }`;

    // Use the inherited 'post' method to ensure the result is wrapped as HttpResult
    return this.post<BaseResponse<any>>(url, payload, {}, true);
  }

  /**
   * @method updateRegisterType
   * @description Updates the registration type details for the current event.
   * @param {registerTypeReqBodyModel} data - The request body containing updated registration type details.
   * @returns {Observable<HttpResult<BaseResponse<registerTypeResModel>>>} Observable containing the response.
   */
  updateRegisterType(
    data: registerTypeReqBodyModel,
    summaryId: any
  ): Observable<HttpResult<BaseResponse<registerTypeResModel>>> {
    let serviceName = `${this.farmerBaseUrl +
      this.farmerId +
      apiEndpoints.farmerRegister.events +
      this.eventcode +
      apiEndpoints.farmerRegister.getAddRegistrationDetails +
      summaryId
      }`;
    return this.put<BaseResponse<registerTypeResModel>>(
      serviceName,
      data,
      {},
      true
    );
  }
}
